import { useCallback } from 'react';
import { Button, FormGroup, Table } from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes } from '../../Common';
import { SaveButton } from '../../common/forms/SaveButton';
import { FlexStartRow, FormLabel, SubHeading } from '../../common/forms/FormElements';
import { TipAlert } from '../../common/TipAlert';
import { Alert, Modal } from '../../common/Modal';
import { PasswordResetModal } from './PasswordResetModal';

export const CustomerPortalAccess = ({
    contactId,
    contactFirstName,
    applicationUserId,
    hasEmail,
    readOnly,
    onToggle,
}) => {
    const { data, isLoading } = useQuery({
        queryKey: ['matchingContacts', contactId],
        queryFn: () => httpClient.js(ApiRoutes.locationContact.matchingContacts(contactId)),
        enabled: Boolean(hasEmail),
    });

    const handleGrantAccessButtonClick = useCallback(() => {
        Modal.show({
            onSubmit: onToggle,
            title: 'Grant Customer Portal Access',
            SaveButtonProps: {
                label: 'Yes, continue',
                icon: faCheck,
                savingLabel: 'Processing...',
            },
            body: () => (
                <>
                    <p>Are you sure you want to grant {contactFirstName} access to the customer portal?</p>

                    <p>
                        This user will be able to view all jobs assigned to any contact sharing their email address, including contacts at other
                        companies.
                    </p>

                    <p>Any unsaved changes to this contact will be saved first.</p>

                    <p>This will also send {contactFirstName} an email with further instructions for logging in to the platform.</p>
                </>
            ),
        });
    }, [onToggle, contactFirstName]);

    const handlePasswordResetLinkClick = useCallback(() => {
        Alert.show({
            body: <PasswordResetModal applicationUserId={applicationUserId} />,
            title: 'Reset Password',
        });
    }, [applicationUserId]);

    if (isLoading) {
        return null;
    }

    return (
        <>
            <FormGroup className="mb-0">
                <FormLabel
                    label="Customer Portal Access"
                />

                {applicationUserId ? (
                    <>
                        <FlexStartRow>
                            <FontAwesomeIcon
                                icon={faCheck}
                                className="text-success mr-2"
                            />

                            This contact has access to the customer portal

                        </FlexStartRow>

                        <Button
                            color="link"
                            className="site-link small mt-1"
                            onClick={handlePasswordResetLinkClick}
                        >
                            Need to reset their password?
                        </Button>
                    </>
                ) : (
                    <div>
                        <TipAlert
                            label={hasEmail ? (
                                <>
                                    {contactFirstName}
                                    {' '}
                                    will be able to see their jobs and request new jobs from the customer portal.
                                </>
                            ) : (
                                <>
                                    {contactFirstName}
                                    {' '}
                                    needs an email address before they can be given access to the customer portal.
                                </>
                            )}
                        />

                        <SaveButton
                            type="button"
                            label="Grant Access to Customer Portal"
                            disabled={!hasEmail || readOnly}
                            onClick={handleGrantAccessButtonClick}
                        />
                    </div>
                )}
            </FormGroup>

            {data?.length > 1 && (
                <>
                    <SubHeading>Matching Contacts</SubHeading>

                    <Table size="sm">
                        <thead>
                            <th>Contact</th>
                            <th>Type(s)</th>
                        </thead>

                        <tbody>
                            {data.map((contact) => (
                                <tr key={contact.value}>
                                    <td>{contact.label}</td>
                                    <td>{contact.id}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </>
            )}
        </>
    );
};